/**
 * "Чеклист" line model — 2026-09-04 (Alexey, verbatim): "добавь в отчет дня
 * анализ по чеклистам, просто раздел 'Чеклист', и там просто две строчки,
 * 🟢 Выполнено: и задачи через запятую, и 🟠 Не выполнено: так же... и
 * должна быть ссылка 'настроить чеклист'".
 *
 * Mirrors services/.../daily_report/sections/checklist.py's
 * `build_checklist_report_section` 1:1: a fixed 🟢 "Выполнено" row, a
 * share-coloured "Не выполнено" row (🟢/🟡/🟠/🔴 — 0% not done → 🟢, <=25%
 * → 🟡, >25% → 🟠, 100% → 🔴; PROPOSED 0%→🟢 band, same status as the
 * backend's own comment), and an optional "настроить чеклист" link row.
 * Unlike the tree blocks in treeBlock.ts this has no ├/└ branch structure —
 * Alexey's spec is explicitly "просто две строчки", flat text — so it's its
 * own small module rather than reusing TreeStatBranch/renderTreeBranches.
 */

const CHECKLIST_EMOJI = '🔴|🟠|🟡|🟢';
const CHECKLIST_DONE_RE = /^🟢\s+Выполнено:\s*([\s\S]*)$/u;
const CHECKLIST_NOT_DONE_RE = new RegExp(
  `^(${CHECKLIST_EMOJI})\\s+Не выполнено:\\s*(?:<u>([\\s\\S]*?)<\\/u>|([\\s\\S]*))$`,
  'u',
);
// Старые отчёты писали «чеклист» слитно — принимаем оба написания и сохраняем
// подпись как была, чтобы сохранение старого отчёта не меняло его текст.
const CHECKLIST_LINK_RE = /^<a\s+href="([^"]*)">\s*([нН]астроить чек-?лист)\s*<\/a>$/u;

export interface ChecklistFields {
  doneNames: string[];
  notDoneNames: string[];
  configureUrl: string; // '' → link row omitted (e.g. hand-edited/legacy row)
  /** Подпись ссылки как в исходном отчёте; нет — «настроить чек-лист». */
  configureLabel?: string;
}

function splitNames(text: string): string[] {
  const t = text.trim();
  if (!t || t === '—') return [];
  return t
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function joinNames(names: string[]): string {
  return names.length > 0 ? names.join(', ') : '—';
}

/** True for the "🟢 Выполнено: ..." header row, IF the immediately following
 *  row is a "Не выполнено" row — same belt-and-braces pairing requirement
 *  treeBlock.ts's isLongestQueueRow/isCategoryTreeRow use for their own
 *  multi-row blocks, so a coincidental single "Выполнено" line elsewhere
 *  (there isn't one today, but nothing enforces that) can't be misread as
 *  a full checklist block. */
export function isChecklistRow(row: string, nextRow: string | undefined): boolean {
  return (
    CHECKLIST_DONE_RE.test(row.trim()) && nextRow !== undefined && CHECKLIST_NOT_DONE_RE.test(nextRow.trim())
  );
}

/** Consume the done row + not-done row (both required, verified present by
 *  isChecklistRow) plus the link row when present (optional — older/
 *  hand-edited bodies may not have it). */
export function consumeChecklistBlockRows(rows: string[], startIdx: number): number {
  let end = startIdx + 1;
  if (end + 1 < rows.length && CHECKLIST_LINK_RE.test(rows[end + 1].trim())) end++;
  return end;
}

export function parseChecklistFields(rows: string[]): ChecklistFields {
  const doneM = CHECKLIST_DONE_RE.exec(rows[0].trim())!;
  const notDoneM = CHECKLIST_NOT_DONE_RE.exec(rows[1].trim())!;
  const notDoneText = notDoneM[2] !== undefined ? notDoneM[2] : (notDoneM[3] ?? '');
  const linkM = rows[2] !== undefined ? CHECKLIST_LINK_RE.exec(rows[2].trim()) : null;
  return {
    doneNames: splitNames(doneM[1]),
    notDoneNames: splitNames(notDoneText),
    configureUrl: linkM ? linkM[1] : '',
    configureLabel: linkM ? linkM[2] : undefined,
  };
}

/** Mirrors build_checklist_report_section's colour/underline logic exactly
 *  — derived from doneNames/notDoneNames, never stored (same convention
 *  treeBlock.ts's serializeNoVisitors uses for its own share-derived
 *  colour). */
export function checklistNotDoneColor(f: Pick<ChecklistFields, 'doneNames' | 'notDoneNames'>): string {
  const total = f.doneNames.length + f.notDoneNames.length;
  if (f.notDoneNames.length === 0) return '🟢';
  if (total > 0 && f.notDoneNames.length === total) return '🔴';
  const share = total > 0 ? f.notDoneNames.length / total : 0;
  return share <= 0.25 ? '🟡' : '🟠';
}

export function serializeChecklist(f: ChecklistFields): string[] {
  const color = checklistNotDoneColor(f);
  const notDoneValue = joinNames(f.notDoneNames);
  const notDoneRendered = f.notDoneNames.length > 0 ? `<u>${notDoneValue}</u>` : notDoneValue;
  const rows = [`🟢 Выполнено: ${joinNames(f.doneNames)}`, `${color} Не выполнено: ${notDoneRendered}`];
  if (f.configureUrl) rows.push(`<a href="${f.configureUrl}">${f.configureLabel ?? 'настроить чек-лист'}</a>`);
  return rows;
}
